// The top bar from design/shell.html: branch selector (the branch-switch dropdown from design/screens/branch-switch.html,
// shown only to users who may switch — UC-IDENTITY-004), the global search, the user block and Lock. The search opens the
// Patients list with the term (`/patients?q=`, UC-PATIENTS-013); invoices and serials join it with their modules.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { auth } from "../api/endpoints";
import { describeError } from "../api/problem";
import { initialsOf } from "../lib/format";
import { lockScreen, loadSession, signOut, useSessionView } from "../session/store";
import { Alert, Chip } from "../ui/bits";
import { Icon } from "../ui/icons";

const ROLE_LABEL: Record<string, string> = { Owner: "Owner", Admin: "Admin", Optometrist: "Optometrist", Dispenser: "Dispenser", FrameConsultant: "Frame consultant", Receptionist: "Receptionist", FranchisorAdmin: "Franchisor admin" };

export function BranchSwitcher() {
  const s = useSessionView();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  if (!s) return null;
  const current = s.switchableBranches.find((b) => b.id === s.activeBranchId) ?? null;
  const currentName = current?.name ?? s.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? "No branch";
  if (!s.canSwitchBranch) return <div className="branch">{currentName}</div>;

  const home = s.assignments.find((a) => a.isHomeBranch)?.branchId ?? s.user.homeBranchId;
  const unassigned = s.assignments.length > 0 ? [] : [];
  const switchTo = async (branchId: string) => {
    setBusy(branchId);
    setError(null);
    try {
      await auth.activeBranch(branchId);
      await loadSession();
      // Every read answers for the acting branch, and not every list names it in its key — a supplier's account, a product's
      // figures, the floors a receipt reads: nothing read for the branch left may stay on screen (the review of 18 Oct 2026, D1-7;
      // TC-IDENTITY-110). What is on screen is read again for this one — a form open meanwhile keeps what was typed — and what is
      // not is dropped, so no screen opened later starts from the other branch's answers.
      qc.removeQueries({ type: "inactive" });
      void qc.invalidateQueries();
      setOpen(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };
  const list = s.switchableBranches;
  return (
    <div className="branch-wrap" ref={wrap}>
      <button type="button" className="branch" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {currentName} <Icon name="chevron" />
      </button>
      {open && (
        <div className="panel dropdown" role="dialog" aria-label="Switch branch">
          <div className="panel-head"><h2>Switch branch</h2><span className="meta">{list.length} of {Math.max(list.length, s.assignments.length)} available to you</span></div>
          {error !== null && <div className="panel-body"><Alert tone="crit"><b>Could not switch.</b> {describeError(error)}</Alert></div>}
          <div className="list" role="listbox" aria-label="Branch">
            {list.map((b) => {
              const isCurrent = b.id === s.activeBranchId;
              const assignment = s.assignments.find((a) => a.branchId === b.id);
              const subParts = [b.code, b.id === home ? "home branch" : null, assignment?.isBranchOwner ? "branch owner" : null].filter(Boolean);
              return (
                <div key={b.id} className="item" role="option" aria-selected={isCurrent}>
                  <div className="grow">
                    <div className="primary">{b.name}</div>
                    <div className="sub"><span className="mono">{subParts[0]}</span>{subParts.slice(1).map((p) => ` · ${p}`).join("")}</div>
                  </div>
                  {isCurrent ? <Chip tone="accent" dot>Current</Chip> : <button className="btn sm" type="button" disabled={busy !== null} onClick={() => void switchTo(b.id)}>{busy === b.id ? "Switching…" : "Switch"}</button>}
                </div>
              );
            })}
            {unassigned.length === 0 && list.length === 0 && <div className="item"><span className="help">No other branch is available to you.</span></div>}
          </div>
          <div className="panel-foot"><span className="help">Switching is recorded in the <Link to="/settings/audit-log">audit log</Link></span></div>
        </div>
      )}
    </div>
  );
}

export function TopBar({ onSearchRef }: { onSearchRef?: ((el: HTMLInputElement | null) => void) | undefined }) {
  const s = useSessionView();
  const [locking, setLocking] = useState(false);
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  if (!s) return null;
  const u = s.user;
  return (
    <header className="top">
      <BranchSwitcher />
      {/* kiosk, today, appointments: UC-PATIENTS-013 A7 (TC-PATIENTS-140) — Enter opens the Patients list with the term as /patients?q= */}
      <form className="search" role="search" onSubmit={(e) => {
        e.preventDefault();
        const q = term.trim();
        if (!q) return;
        setTerm("");
        (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.blur();
        navigate(`/patients?q=${encodeURIComponent(q)}`);
      }}>
        <Icon name="search" />
        <input ref={onSearchRef} type="search" aria-label="Search" placeholder="Search patients by name, account code, phone or ID…" value={term} onChange={(e) => setTerm(e.target.value)} />
        <kbd>/</kbd>
      </form>
      {/* end kiosk, today, appointments */}
      <div className="who">
        <button className="iconbtn" type="button" aria-label="Notifications" title="Notifications arrive in a later sprint"><Icon name="bell" /></button>
        <div className="u"><b>{u.displayName}</b><span>{ROLE_LABEL[u.role] ?? u.role} · Control ID <span className="mono">{u.controlIdDisplay}</span></span></div>
        {/* Below 1 000 px the name and role above give way (app.css); the avatar then says who is signed in. */}
        <span className="avatar" role="img" aria-label={`Signed in as ${u.displayName}, ${ROLE_LABEL[u.role] ?? u.role}, Control ID ${u.controlIdDisplay}`} title={`${u.displayName} · ${ROLE_LABEL[u.role] ?? u.role} · Control ID ${u.controlIdDisplay}`}>{initialsOf(u.firstName, u.lastName)}</span>
        <button className="btn" type="button" aria-label="Lock screen" disabled={locking} onClick={() => { setLocking(true); void lockScreen().finally(() => setLocking(false)); }}><Icon name="lock" />Lock</button>
        {/* Sign out beside Lock (UC-IDENTITY-002 step 1): before, the only way out was Lock › "Not you? Switch user" (testing of
            12 Oct 2026, finding 5). The same call — offline it shows sign-in and remembers to end the session (A2). */}
        <button className="btn ghost" type="button" disabled={locking} onClick={() => void signOut()}>Sign out</button>
      </div>
    </header>
  );
}
